<script setup lang="ts">
const companionStore = useCompanion();
const {
  profiles,
  selectedProfileId,
  isLoadingProfiles,
  isCreatingProfile,
  hasInitializedProfiles,
} = storeToRefs(companionStore);
const appUserStore = useAppUserDataStore();
const { isInitialized: isAppUserInitialized } = storeToRefs(appUserStore);
const route = useRoute();

const activeDocumentId = computed(() => {
  const documentId = route.params.documentId;
  return typeof documentId === "string" ? documentId : undefined;
});

const activeSubmittedDocument = computed(() => {
  if (!activeDocumentId.value) return undefined;

  return useSubmittedDocumentStore(activeDocumentId.value).submittedDocument;
});

const isLoadingActiveDocument = computed(() => {
  return (
    activeDocumentId.value != undefined &&
    activeSubmittedDocument.value == undefined
  );
});

watch(
  [activeDocumentId, activeSubmittedDocument, hasInitializedProfiles],
  ([documentId, submittedDocument, profilesInitialized]) => {
    if (!documentId || !submittedDocument || profilesInitialized != true) {
      return;
    }

    companionStore.selectDocumentProfile(submittedDocument.profileId);
  },
  { immediate: true }
);

const avatars = computed(() => {
  return useCompanionAvatars().sort((a, b) => a.displayOrder - b.displayOrder);
});

const isAddingProfile = ref(false);
const profileName = ref("");
const selectedAvatarHref = ref(avatars.value[0]?.href ?? "");

const isInitializingProfiles = computed(() => {
  return (
    isAppUserInitialized.value != true ||
    hasInitializedProfiles.value != true ||
    isLoadingProfiles.value ||
    isLoadingActiveDocument.value
  );
});

const isCreateMode = computed(() => {
  return (
    (hasInitializedProfiles.value == true && profiles.value.length === 0) ||
    isAddingProfile.value
  );
});

const title = computed(() => {
  if (isInitializingProfiles.value) return "Loading Profiles";
  if (hasInitializedProfiles.value == true && profiles.value.length === 0)
    return "Create Profile";
  if (isCreateMode.value) return "Add Profile";
  return "Choose Profile";
});

const description = computed(() => {
  if (isInitializingProfiles.value) return "Getting companion ready...";
  if (hasInitializedProfiles.value == true && profiles.value.length === 0)
    return "Set up a profile before using companion.";
  if (isCreateMode.value) return "Choose a name and avatar for this profile.";
  return "Select who is using companion today.";
});

const startAddingProfile = () => {
  profileName.value = "";
  selectedAvatarHref.value = avatars.value[0]?.href ?? "";
  isAddingProfile.value = true;
};

const cancelAddingProfile = () => {
  if (profiles.value.length === 0) return;

  isAddingProfile.value = false;
};

const hideBrokenImage = (event: Event) => {
  (event.target as HTMLImageElement).classList.add("hidden");
};

const createProfile = async () => {
  await companionStore.createProfile({
    name: profileName.value,
    avatarHref: selectedAvatarHref.value,
  });

  if (selectedProfileId.value) {
    isAddingProfile.value = false;
    profileName.value = "";
  }
};
</script>

<template>
  <div
    v-if="!selectedProfileId || isInitializingProfiles"
    class="absolute inset-0 z-[100000] flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
  >
    <div
      class="w-full max-w-[620px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200"
    >
      <div class="border-b border-gray-100 px-6 py-5">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-950">{{ title }}</h2>
          </div>
        </div>
      </div>

      <div
        v-if="isInitializingProfiles"
        class="flex min-h-[280px] items-center justify-center p-8"
      >
        <div class="flex items-center gap-3 text-gray-600">
          <BaseLoadingSpinner class="h-8 w-8 text-primary" />
          <span class="text-sm font-medium">Loading profiles...</span>
        </div>
      </div>

      <div v-else-if="isCreateMode" class="p-6">
        <div class="space-y-5">
          <label class="block">
            <span class="mb-2 block text-sm font-semibold text-gray-800"
              >Name</span
            >
            <BaseTextInput
              v-model="profileName"
              class="w-full"
              @keyup.enter="createProfile"
            />
          </label>

          <div>
            <span class="mb-3 block text-sm font-semibold text-gray-800"
              >Avatar</span
            >
            <div
              class="grid max-h-[320px] grid-cols-3 gap-3 overflow-y-auto pr-1 sm:grid-cols-6"
            >
              <button
                v-for="avatar in avatars"
                :key="avatar.href"
                type="button"
                class="relative aspect-square rounded-xl border-2 bg-gray-50 p-2 transition hover:border-primary/50 hover:bg-primary/5"
                :class="
                  selectedAvatarHref === avatar.href
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-gray-200'
                "
                @click="selectedAvatarHref = avatar.href"
              >
                <span
                  class="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-300"
                >
                  {{ avatar.displayOrder }}
                </span>
                <img
                  :src="avatar.href"
                  :alt="`Avatar ${avatar.displayOrder}`"
                  class="relative z-10 h-full w-full object-contain"
                  @error="hideBrokenImage"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <BaseButton
            v-if="profiles.length > 0"
            color="secondary"
            :disabled="isCreatingProfile"
            @click="cancelAddingProfile"
          >
            Cancel
          </BaseButton>
          <BaseButton :show-spinner="isCreatingProfile" @click="createProfile">
            Create Profile
          </BaseButton>
        </div>
      </div>

      <div v-else class="p-6">
        <div class="grid gap-3 sm:grid-cols-2">
          <button
            v-for="profile in profiles"
            :key="profile.id"
            type="button"
            class="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm"
            @click="companionStore.selectProfile(profile.id)"
          >
            <div
              class="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gray-100"
            >
              <span class="text-xl font-bold text-gray-300">
                {{ profile.name.charAt(0).toUpperCase() }}
              </span>
              <img
                :src="profile.avatarHref"
                :alt="profile.name"
                class="absolute inset-0 h-full w-full rounded-full object-contain"
                @error="hideBrokenImage"
              />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-base font-bold text-gray-950">
                {{ profile.name }}
              </div>
            </div>
            <BaseIconChevronRight class="h-5 w-5 shrink-0 text-gray-400" />
          </button>

          <button
            type="button"
            class="group flex items-center gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-left transition hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm"
            @click="startAddingProfile"
          >
            <div
              class="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition group-hover:border-primary/30 group-hover:text-primary"
            >
              <BaseIconPlus class="h-8 w-8" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-base font-bold text-gray-950">
                Add New Profile
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
